import { QUERY_KEYS } from "@/libraries/constant";
import { getIsoDateTime } from "@/libraries/date";
import { randInt } from "@/libraries/helper";
import type {
	PutReminderCheckBodySchema,
	ReminderFormFieldSchema,
} from "@/schemas";
import { reminderService } from "@/services";
import { eventService } from "@/services/event";
import type {
	GetReminderResponse,
	GetRemindersResponse,
	Reminder,
} from "@/types";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import { infiniteDataRemove } from "../utils/mutation";

const putCheckReminder = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_REMINDER_CHECK], {
		retry: 2,
		mutationFn: (payload: PutReminderCheckBodySchema) =>
			reminderService.check(payload),
		onMutate: async ({ id, is_done }) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_REMINDERS],
			});
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_REMINDER, id],
			});
			queryClient.setQueryData<InfiniteData<GetRemindersResponse>>(
				[QUERY_KEYS.GET_REMINDERS],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							reminders: p.reminders.map((r) =>
								r.id === id
									? {
											...r,
											is_done,
										}
									: r,
							),
						})) || [],
					pageParams: oldData?.pageParams ?? [1],
				}),
			);
			queryClient.setQueryData<GetReminderResponse>(
				[QUERY_KEYS.GET_REMINDER, id],
				(oldData) =>
					oldData
						? {
								...oldData,
								reminder: {
									...oldData.reminder,
									is_done,
								},
							}
						: undefined,
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_MEMBER_HOME],
			});
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CALENDARS_DETAIL],
				exact: false,
			});
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CALENDAR_INFO],
				exact: false,
			});
		},
		onError: (_err, { id, is_done }) => {
			queryClient.setQueryData<InfiniteData<GetRemindersResponse>>(
				[QUERY_KEYS.GET_REMINDERS],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							reminders: p.reminders.map((r) =>
								r.id === id
									? {
											...r,
											is_done: !is_done,
										}
									: r,
							),
						})) || [],
					pageParams: oldData?.pageParams ?? [1],
				}),
			);
			queryClient.setQueryData<GetReminderResponse>(
				[QUERY_KEYS.GET_REMINDER, id],
				(oldData) =>
					oldData
						? {
								...oldData,
								reminder: {
									...oldData.reminder,
									is_done: !is_done,
								},
							}
						: undefined,
			);
		},
	});
};

const postCreateReminder = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_REMINDER_CREATE], {
		retry: 2,
		mutationFn: (payload: ReminderFormFieldSchema) =>
			reminderService.create(payload),
		onMutate: async (newReminder) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_REMINDERS],
			});
			// @ts-expect-error
			const optimisticReminder: Reminder = {
				...newReminder,
				id: randInt(),
				priority: null,
				is_done: false,
				created_at: getIsoDateTime(),
				updated_at: getIsoDateTime(),
			};
			queryClient.setQueryData<InfiniteData<GetRemindersResponse>>(
				[QUERY_KEYS.GET_REMINDERS],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							reminders: [optimisticReminder, ...p.reminders],
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);

			return { optimisticReminder };
		},
		onSuccess: (res, _, ctx) => {
			if (!ctx.optimisticReminder || !res) return;
			queryClient.setQueryData<InfiniteData<GetRemindersResponse>>(
				[QUERY_KEYS.GET_REMINDERS],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							reminders: p.reminders.map((r) =>
								r.id === ctx.optimisticReminder.id ? res.reminder : r,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
		},
		onError: (_err, _newReminder, ctx) => {
			if (!ctx?.optimisticReminder) return;
			queryClient.setQueryData<InfiniteData<GetRemindersResponse>>(
				[QUERY_KEYS.GET_REMINDERS],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							reminders: p.reminders.filter(
								(r) => r.id !== ctx.optimisticReminder.id,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
		},
	});
};

const patchUpdateReminder = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_REMINDER_UPDATE], {
		retry: 2,
		mutationFn: (payload: ReminderFormFieldSchema) =>
			reminderService.update(payload),
		onMutate: async (updatedReminder) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_REMINDERS],
			});
			const oldReminder = queryClient.getQueryData<GetReminderResponse>([
				QUERY_KEYS.GET_REMINDER,
				updatedReminder.id,
			]);
			if (!oldReminder) return;
			queryClient.setQueryData<InfiniteData<GetRemindersResponse>>(
				[QUERY_KEYS.GET_REMINDERS],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							reminders: p.reminders.map((r) =>
								r.id === updatedReminder.id ? (updatedReminder as Reminder) : r,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			queryClient.setQueryData<GetReminderResponse>(
				[QUERY_KEYS.GET_REMINDER, updatedReminder.id],
				(oldData) =>
					oldData
						? {
								...oldData,
								reminder: updatedReminder as Reminder,
							}
						: undefined,
			);

			return { oldReminder, updatedReminder };
		},
		onSuccess: (res, _, ctx) => {
			if (!ctx.updatedReminder || !res) return;
			queryClient.setQueryData<InfiniteData<GetRemindersResponse>>(
				[QUERY_KEYS.GET_REMINDERS],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							reminders: p.reminders.map((r) =>
								r.id === ctx.updatedReminder.id ? res.reminder : r,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			queryClient.setQueryData<GetReminderResponse>(
				[QUERY_KEYS.GET_REMINDER, ctx.updatedReminder.id],
				(oldData) =>
					oldData
						? {
								...oldData,
								reminder: res.reminder,
							}
						: undefined,
			);
		},
		onError: (_err, _updatedReminder, ctx) => {
			if (!ctx?.oldReminder) return;
			queryClient.setQueryData<InfiniteData<GetRemindersResponse>>(
				[QUERY_KEYS.GET_REMINDERS],
				(oldData) => ({
					...oldData,
					pages:
						oldData?.pages?.map((p) => ({
							...p,
							reminders: p.reminders.map((r) =>
								r.id === ctx.oldReminder.reminder.id
									? ctx.oldReminder.reminder
									: r,
							),
						})) || [],
					pageParams: oldData?.pageParams || [1],
				}),
			);
			queryClient.setQueryData<GetReminderResponse>(
				[QUERY_KEYS.GET_REMINDER, ctx.oldReminder.reminder.id],
				(oldData) =>
					oldData
						? {
								...oldData,
								reminder: ctx.oldReminder.reminder,
							}
						: undefined,
			);
		},
	});
};

const deleteReminder = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_REMINDER_DELETE], {
		retry: 2,
		mutationFn: (id: number) => reminderService.delete(id),
		onMutate: async (reminderId) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_REMINDER, reminderId],
			});
			const currentReminder = queryClient.getQueryData<GetReminderResponse>([
				QUERY_KEYS.GET_REMINDER,
				reminderId,
			]);
			if (!currentReminder) return;
			queryClient.removeQueries({
				queryKey: [QUERY_KEYS.GET_REMINDER, reminderId],
			});
			return { currentReminder };
		},
		onSuccess: (_, reminderId) => {
			queryClient.setQueryData<InfiniteData<GetRemindersResponse>>(
				[QUERY_KEYS.GET_REMINDERS],
				(odlData) => infiniteDataRemove(odlData, "reminders", "id", reminderId),
			);
		},
		onError: (_, reminderId, ctx) => {
			if (!ctx) return;
			queryClient.setQueryData<GetReminderResponse>(
				[QUERY_KEYS.GET_REMINDER, reminderId],
				ctx.currentReminder,
			);
		},
	});
};

const postCreateEvent = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_EVENT_CREATE], {
		retry: 2,
		mutationFn: (payload: Parameters<typeof eventService.createEvent>[0]) =>
			eventService.createEvent(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CALENDARS_DETAIL],
				exact: false,
			});
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CALENDAR_INFO],
				exact: false,
			});
		},
	});
};

const patchUpdateEvent = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_EVENT_UPDATE], {
		retry: 2,
		mutationFn: (payload: Parameters<typeof eventService.updateEvent>[0]) =>
			eventService.updateEvent(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CALENDARS_DETAIL],
				exact: false,
			});
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CALENDAR_INFO],
				exact: false,
			});
		},
	});
};

const removeEvent = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_EVENT_REMOVE], {
		retry: 2,
		mutationFn: (payload: Parameters<typeof eventService.deleteEvent>[0]) =>
			eventService.deleteEvent(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CALENDARS_DETAIL],
				exact: false,
			});
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CALENDAR_INFO],
				exact: false,
			});
		},
	});
};

const copyEvent = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_EVENT_COPY], {
		retry: 2,
		mutationFn: (payload: Parameters<typeof eventService.copyEvent>[0]) =>
			eventService.copyEvent(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CALENDARS_DETAIL],
				exact: false,
			});
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_CALENDAR_INFO],
				exact: false,
			});
		},
	});
};

export const reminderMutations = (queryClient: QueryClient) => {
	putCheckReminder(queryClient);
	postCreateReminder(queryClient);
	patchUpdateReminder(queryClient);
	deleteReminder(queryClient);
	patchUpdateEvent(queryClient);
	postCreateEvent(queryClient);
	removeEvent(queryClient);
	copyEvent(queryClient);
};
